import { api } from "./api";
import { setGeo, userStore } from "../redux/user";
import { v4 } from "uuid";

import {
  doStartReconnectTimer,
  endReconnectTimer,
  setOtherLoginShow,
  setWsConnected,
  updateReconnectTimer,
  wsConnectionStore,
  setWebsocketUrl,
} from "../redux/wsConnection";

import { routeIncommingMessage } from "./wsInRoutes";
import { LoginWs } from "./wsOutRoutes";
import { wsDebug } from "./wsDebug";
import {
  reportRussiaSiteSettings,
  reportRussiaSiteSettingsFailed,
  reportRussiaWebsocketEvent,
} from "./clientHealth";

let offlineDetectionInitialized = false;
let offlineTimeOut;

export let websocket = null;
let pingInterval: any;
let postTimeout: any;
export let longPollingUrl = null;

const PING_INTERVAL_MS = 5000;
const PONG_STALE_STEPS_MS = [8000, 15000, 22000, 30000];
const PONG_STALE_BACKOFF_WINDOW_MS = 120000;
const PONG_STALE_STABLE_RESET_MS = 300000;
const WATCHDOG_RECONNECT_COOLDOWN_MS = 8000;
const WATCHDOG_RECOVERY_PENDING_MS = 30000;
const WATCHDOG_RECOVERY_REPORT_DEDUPE_MS = 30000;
const CLIENT_RECONNECT_CLOSE_CODE = 4000;

let lastPingAt = 0;
let lastPongAt = 0;
let websocketOpenedAt = 0;
let watchdogReconnectCount = 0;
let lastWatchdogReconnectAt = 0;
let pendingWatchdogRecovery: {
  id: string;
  reason: string;
  startedAt: number;
  openedAt: number | null;
  beforeReconnect: Record<string, any>;
  afterOpen?: Record<string, any>;
} | null = null;
let lastWatchdogRecoveryReportKey = "";
let lastWatchdogRecoveryReportAt = 0;
let websocketUserKey = "";
let stalePongReconnectCount = 0;
let firstStalePongReconnectAt = 0;
let lastStalePongReconnectAt = 0;

function getWebsocketUserKey() {
  const { username, isGuest, id, room } = userStore.getState();
  return [username || "", isGuest ? "guest" : "member", id || "", room || ""]
    .map((value) => String(value))
    .join(":");
}

function getBrowserConnectionInfo() {
  if (!process.browser) {
    return {};
  }

  const nav = navigator as any;
  const connection =
    nav.connection || nav.mozConnection || nav.webkitConnection || {};

  return {
    networkState: navigator.onLine ? "online" : "offline",
    visibilityState: document.visibilityState,
    effectiveType: connection.effectiveType || null,
    downlink: connection.downlink || null,
    rtt: connection.rtt || null,
    saveData: connection.saveData || false,
  };
}

function getPongStaleThresholdMs(now = Date.now()) {
  if (
    !lastStalePongReconnectAt ||
    now - lastStalePongReconnectAt > PONG_STALE_STABLE_RESET_MS
  ) {
    return PONG_STALE_STEPS_MS[0];
  }

  const stepIndex = Math.min(
    stalePongReconnectCount,
    PONG_STALE_STEPS_MS.length - 1
  );
  return PONG_STALE_STEPS_MS[stepIndex];
}

function resetPongStaleBackoff() {
  stalePongReconnectCount = 0;
  firstStalePongReconnectAt = 0;
  lastStalePongReconnectAt = 0;
}

function maybeResetPongStaleBackoff(now = Date.now()) {
  if (
    lastStalePongReconnectAt &&
    now - lastStalePongReconnectAt > PONG_STALE_STABLE_RESET_MS
  ) {
    resetPongStaleBackoff();
  }
}

function noteStalePongReconnect(now = Date.now()) {
  if (
    !firstStalePongReconnectAt ||
    now - firstStalePongReconnectAt > PONG_STALE_BACKOFF_WINDOW_MS
  ) {
    firstStalePongReconnectAt = now;
    stalePongReconnectCount = 0;
  }

  stalePongReconnectCount += 1;
  lastStalePongReconnectAt = now;
}

function setPingErrorData(update: {
  eventSequence?: Record<string, any>;
  websocketWatchdog?: Record<string, any>;
  [key: string]: any;
}) {
  if (process.env.NODE_ENV === "development") {
    return;
  }

  import("../redux/ping").then((module) => {
    const state = module.pingStore.getState();
    module.setErrorData({
      ...update,
      ...(update.eventSequence && {
        eventSequence: {
          ...state.errorData.eventSequence,
          ...update.eventSequence,
        },
      }),
      ...(update.websocketWatchdog && {
        websocketWatchdog: {
          ...state.errorData.websocketWatchdog,
          ...update.websocketWatchdog,
        },
      }),
    });
  });
}

function getWatchdogState(reason: string, now = Date.now()) {
  return {
    reason,
    lastCheckAt: now,
    lastPingAt: lastPingAt || null,
    lastPongAt: lastPongAt || null,
    websocketOpenedAt: websocketOpenedAt || null,
    stalePongMs: lastPongAt ? now - lastPongAt : null,
    pongStaleThresholdMs: getPongStaleThresholdMs(now),
    stalePongReconnectCount,
    lastStalePongReconnectAt: lastStalePongReconnectAt || null,
    openedForMs: websocketOpenedAt ? now - websocketOpenedAt : null,
    readyState: websocket?.readyState ?? null,
    url: websocket?.url || null,
    reconnectCount: watchdogReconnectCount,
    ...getBrowserConnectionInfo(),
  };
}

function recordWatchdogState(reason: string, now = Date.now()) {
  setPingErrorData({
    websocketWatchdog: getWatchdogState(reason, now),
  });
}

function getCompactWatchdogState(state?: Record<string, any> | null) {
  if (!state) {
    return null;
  }

  return {
    reason: state.reason,
    lastCheckAt: state.lastCheckAt,
    stalePongMs: state.stalePongMs,
    pongStaleThresholdMs: state.pongStaleThresholdMs,
    stalePongReconnectCount: state.stalePongReconnectCount,
    openedForMs: state.openedForMs,
    readyState: state.readyState,
    reconnectCount: state.reconnectCount,
    networkState: state.networkState,
    visibilityState: state.visibilityState,
    effectiveType: state.effectiveType,
    rtt: state.rtt,
  };
}

function rememberWatchdogRecoveryAttempt(reason: string, now: number) {
  pendingWatchdogRecovery = {
    id: `${now}-${watchdogReconnectCount}`,
    reason,
    startedAt: now,
    openedAt: null,
    beforeReconnect: getWatchdogState(reason, now),
  };
}

function markWatchdogRecoveryOpened(now: number) {
  if (!pendingWatchdogRecovery) {
    return;
  }

  if (now - pendingWatchdogRecovery.startedAt > WATCHDOG_RECOVERY_PENDING_MS) {
    pendingWatchdogRecovery = null;
    return;
  }

  pendingWatchdogRecovery = {
    ...pendingWatchdogRecovery,
    openedAt: now,
    afterOpen: getWatchdogState("websocket-open", now),
  };
}

export function reportWatchdogGameStateRecovered({
  gameId,
  isPlaying,
  halfMoveCount,
  status,
  result,
}: {
  gameId?: string | null;
  isPlaying?: boolean | null;
  halfMoveCount?: number | null;
  status?: number | string | null;
  result?: number | string | null;
}) {
  if (!process.browser || !pendingWatchdogRecovery || !gameId) {
    return;
  }

  const now = Date.now();
  const recovery = pendingWatchdogRecovery;
  if (now - recovery.startedAt > WATCHDOG_RECOVERY_PENDING_MS) {
    pendingWatchdogRecovery = null;
    return;
  }

  const key = `${recovery.id}:${gameId}:game-info`;
  if (
    lastWatchdogRecoveryReportKey === key &&
    now - lastWatchdogRecoveryReportAt < WATCHDOG_RECOVERY_REPORT_DEDUPE_MS
  ) {
    return;
  }

  lastWatchdogRecoveryReportKey = key;
  lastWatchdogRecoveryReportAt = now;
  pendingWatchdogRecovery = null;

  import("../redux/ping")
    .then((module) => {
      module.sendWebsocketWatchdogRecovery({
        phase: "game-info",
        timestamp: now,
        sessionId: getOrCreateSessionId(),
        recovery: {
          id: recovery.id,
          reason: recovery.reason,
          startedAt: recovery.startedAt,
          openedAt: recovery.openedAt,
          reportedAt: now,
          msToOpen: recovery.openedAt
            ? recovery.openedAt - recovery.startedAt
            : null,
          msToGameInfo: now - recovery.startedAt,
          beforeReconnect: getCompactWatchdogState(recovery.beforeReconnect),
          afterOpen: getCompactWatchdogState(recovery.afterOpen),
        },
        game: {
          gameId,
          isPlaying: isPlaying ?? null,
          halfMoveCount: halfMoveCount ?? null,
          status: status ?? null,
          result: result ?? null,
        },
      });
    })
    .catch(() => {
      console.log("load ping reporter failed");
    });
}

function isPongStale(now = Date.now()) {
  if (!websocketOpenedAt) {
    return false;
  }

  const lastHealthyAt = lastPongAt || websocketOpenedAt;
  return now - lastHealthyAt > getPongStaleThresholdMs(now);
}

function reconnectFromWatchdog(reason: string) {
  const now = Date.now();

  if (now - lastWatchdogReconnectAt < WATCHDOG_RECONNECT_COOLDOWN_MS) {
    recordWatchdogState(`${reason}:cooldown`, now);
    return;
  }

  if (reason.indexOf("stale-pong") !== -1) {
    noteStalePongReconnect(now);
  }

  watchdogReconnectCount++;
  lastWatchdogReconnectAt = now;
  rememberWatchdogRecoveryAttempt(reason, now);
  startReconnectTimer();
  setWsConnected(false);
  recordWatchdogState(reason, now);

  wsDebug("watchdog reconnect", getWatchdogState(reason, now));
  initWebsocketConnect();
}

function recoverIfWebsocketStale(reason: string) {
  if (!process.browser) {
    return;
  }

  if (!websocket) {
    reconnectFromWatchdog(`${reason}:missing-socket`);
    return;
  }

  if (websocket.readyState === WebSocket.CONNECTING) {
    recordWatchdogState(`${reason}:connecting`);
    return;
  }

  if (websocket.readyState !== WebSocket.OPEN) {
    reconnectFromWatchdog(`${reason}:not-open`);
    return;
  }

  if (isPongStale()) {
    reconnectFromWatchdog(`${reason}:stale-pong`);
  }
}

function setupOfflineDetection() {
  if (offlineDetectionInitialized) return;
  offlineDetectionInitialized = true;

  window.addEventListener("offline", (event) => {
    import("../redux/ping").then((module) => {
      module.setOffline();
      module.setErrorData({
        networkState: "offline",
        networkStateTime: Date.now(),
      });

      offlineTimeOut = setTimeout(() => {
        startReconnectTimer();
      }, 3000);
    });
  });

  window.addEventListener("online", (event) => {
    import("../redux/ping").then((module) => {
      module.setErrorData({
        networkState: "online",
        networkStateTime: Date.now(),
      });
      module.resetRetries();
      clearTimeout(offlineTimeOut);
      endReconnectTimer();
      initWebsocketConnect();
    });
  });

  window.addEventListener("focus", () => {
    recoverIfWebsocketStale("focus");
  });

  window.addEventListener("pageshow", () => {
    recoverIfWebsocketStale("pageshow");
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      recoverIfWebsocketStale("visibilitychange");
    }
  });
}

export function getOrCreateSessionId() {
  let sessionId: string = sessionStorage.getItem("sessionId");
  if (!sessionId) {
    sessionId = v4();
    sessionStorage.setItem("sessionId", sessionId);
  }
  return sessionId;
}

async function loginServer() {
  const { username, isGuest, id, room } = userStore.getState();
  import("../redux/gamePlay").then(({ gamePlayStore }) => {
    const { gameId } = gamePlayStore.getState();

    try {
      const sessionId = getOrCreateSessionId();

      const user = {
        username,
        isGuest,
        userId: id,
        room,
        gameId,
        sessionId,
      };

      LoginWs.loginServer(user);
      reportRussiaWebsocketEvent("websocket_login_sent", {
        isGuest,
        userId: id || null,
        room,
        gameId: gameId || null,
      });
    } catch (e) {
      setTimeout(() => {
        loginServer();
      }, 3000);
    }
  });
}

function startReconnectTimer() {
  const reconnectStart = wsConnectionStore.getState().reconnectStart;
  if (!reconnectStart) {
    doStartReconnectTimer();

    const loop = () => {
      const startTime = wsConnectionStore.getState().reconnectStart;
      if (startTime == null) {
        return;
      }
      updateReconnectTimer();
      requestAnimationFrame(loop);
    };

    loop();
  }
}

export function initWebsocketConnect() {
  wsDebug("initWebsocketConnect: fetching site settings", {
    baseURL: api.defaults.baseURL,
  });

  api
    .get(`/site/settings?_=${new Date().getTime()}`)
    .then(async (response) => {
      const { ws, geo } = response.data;
      wsDebug("site/settings response", {
        ws,
        geo,
        host: process.env.NEXT_PUBLIC_HOST,
      });
      reportRussiaSiteSettings({ geo, ws });

      setWebsocketUrl(ws);
      connectToWebSocket();
      const localGeo = localStorage.getItem("geo");
      if (localGeo) {
        setGeo(localGeo);
      } else {
        setGeo(geo);
      }
    })
    .catch((error) => {
      console.log("get ws number error", error);
      wsDebug("site/settings request failed", {
        message: error?.message,
        status: error?.response?.status,
      });
      reportRussiaSiteSettingsFailed(error);
      setTimeout(() => {
        initWebsocketConnect();
      }, 1000);
    });
}

//Happens when server code update happens and it switches to a new server instance
export async function checkWs(ws: number) {
  import("../redux/gamePlay").then(({ gamePlayStore }) => {
    const { gameEnded } = gamePlayStore.getState();

    const isPlaying = !gameEnded;
    if (isPlaying) {
      const unsubscribe = gamePlayStore.subscribe(({ gameEnded }) => {
        if (gameEnded && wsConnectionStore.getState().ws !== ws) {
          setTimeout(() => {
            unsubscribe();
            setWebsocketUrl(ws);
            connectToWebSocket();
          }, 2000);
        }
      });
    }

    if (!isPlaying && wsConnectionStore.getState().ws !== ws) {
      setWebsocketUrl(ws);
      connectToWebSocket();
    }
  });
}

export function otherUserLogin() {
  disconnectWebSocket("other login");
  setOtherLoginShow(true);
}

function disconnectWebSocket(reason = "client reconnect") {
  clearInterval(pingInterval);

  if (websocket) {
    websocket.onclose = function () {};
    try {
      websocket.close(CLIENT_RECONNECT_CLOSE_CODE, reason);
    } catch (e) {
      websocket.close();
    }
  }
}

let retries = 0;
const LIVE_CLOSE_REPORT_DEDUPE_MS = 30000;
let lastLiveCloseReportKey = "";
let lastLiveCloseReportAt = 0;

export function incrRetries() {
  retries++;
}

function shouldReportLiveClose(
  gameId: string | null,
  code: number,
  now: number,
) {
  const key = `${gameId || "no-game"}:${code}`;
  if (
    lastLiveCloseReportKey === key &&
    now - lastLiveCloseReportAt < LIVE_CLOSE_REPORT_DEDUPE_MS
  ) {
    return false;
  }

  lastLiveCloseReportKey = key;
  lastLiveCloseReportAt = now;
  return true;
}

function connectToWebSocket() {
  const url = wsConnectionStore.getState().websocketUrl;
  const userKey = getWebsocketUserKey();

  if (
    websocket &&
    websocket.url === url &&
    websocketUserKey === userKey &&
    (websocket.readyState === WebSocket.OPEN ||
      websocket.readyState === WebSocket.CONNECTING) &&
    !isPongStale()
  ) {
    wsDebug("connectToWebSocket: reusing existing socket", {
      url,
      readyState: websocket.readyState,
    });
    return;
  }

  disconnectWebSocket("client reconnect");

  setWsConnected(false);
  websocketOpenedAt = 0;
  lastPingAt = 0;
  lastPongAt = 0;
  websocketUserKey = userKey;

  wsDebug("connectToWebSocket", { url });
  websocket = new window.WebSocket(url);

  websocket.onerror = (error: Event) => {
    wsDebug("websocket error", {
      readyState: websocket?.readyState,
      url: websocket?.url,
    });
    reportRussiaWebsocketEvent("websocket_error", {
      readyState: websocket?.readyState,
      url: websocket?.url,
    });

    import("../redux/ping").then((module) => {
      module.setWsError();
      module.setErrorData({
        lastWebsocketError: {
          readyState: websocket?.readyState,
          url: websocket?.url,
          timestamp: Date.now(),
        },
      });
    });
  };

  websocket.onclose = (e: CloseEvent) => {
    console.log("websocket close");
    wsDebug("websocket close", {
      code: e.code,
      reason: e.reason,
      wasClean: e.wasClean,
      readyState: websocket?.readyState,
      url: websocket?.url,
    });
    reportRussiaWebsocketEvent("websocket_close", {
      code: e.code,
      reason: e.reason,
      wasClean: e.wasClean,
      readyState: websocket?.readyState,
      url: websocket?.url,
      retries,
    });

    import("../redux/ping").then((module) => {
      import("../redux/gamePlay").then(({ gamePlayStore }) => {
        const now = Date.now();
        const pingState = module.pingStore.getState();
        const closeReadyState = websocket?.readyState ?? null;
        const closeUrl = websocket?.url || null;
        module.setWsClose();
        module.setWsCloseErrorMsg(e.code + " " + e.reason);
        module.setDisconnectTime();
        module.setErrorData({
          eventSequence: {
            ...pingState.errorData.eventSequence,
            lastWsClose: now,
            timeSinceLastVisibilityChange:
              now -
              (pingState.errorData.connectionState.lastVisibilityChange || 0),
            timeSinceLastPing:
              now - (pingState.errorData.eventSequence.lastPingSent || 0),
            timeSinceLastPong:
              now - (pingState.errorData.eventSequence.lastPongReceived || 0),
          },
          networkStateOnClose:
            process.browser && navigator.onLine ? "online" : "offline",
          networkStateTimeOnClose: now,
          websocketWatchdog: {
            ...pingState.errorData.websocketWatchdog,
            ...getWatchdogState("websocket-close", now),
          },
        });

        const { gameEnded, gameId } = gamePlayStore.getState();
        const isPlaying = gameEnded == false;
        module.setErrorData({
          wsCloseIsPlaying: isPlaying,
          wsCloseTime: now,
          wsCloseGameId: gameId,
        });

        if (isPlaying && gameId && shouldReportLiveClose(gameId, e.code, now)) {
          module.sendWebsocketCloseError({
            code: e.code,
            reason: e.reason,
            wasClean: e.wasClean,
            readyState: closeReadyState,
            url: closeUrl,
            gameId,
            isPlaying,
            timestamp: now,
            sessionId: getOrCreateSessionId(),
          });
        }

        setTimeout(() => {
          module.setRetries();
          retries++;
          startReconnectTimer();
          initWebsocketConnect();
        }, 1000);
      });
    });
  };
  websocket.onopen = () => {
    console.log("websocket open");
    wsDebug("websocket open", {
      readyState: websocket?.readyState,
      url: websocket?.url,
    });
    reportRussiaWebsocketEvent("websocket_open", {
      readyState: websocket?.readyState,
      url: websocket?.url,
      retries,
    });

    setWsConnected(true);

    endReconnectTimer();
    websocketOpenedAt = Date.now();
    lastPongAt = websocketOpenedAt;
    recordWatchdogState("websocket-open", websocketOpenedAt);
    markWatchdogRecoveryOpened(websocketOpenedAt);
    startPing();
    websocket.sendPing();

    import("../redux/lobby").then((module) => {
      module.clearInvites();
      module.enterLobby();
    });

    loginServer();
    setupOfflineDetection();
  };

  websocket.onmessage = async (e) => {
    if (e.data === "pong") {
      lastPongAt = Date.now();
      maybeResetPongStaleBackoff(lastPongAt);
      reportRussiaWebsocketEvent("websocket_pong", {
        latencyMs: lastPingAt ? lastPongAt - lastPingAt : null,
        url: websocket?.url,
      });
      setPingErrorData({
        eventSequence: {
          lastPongReceived: lastPongAt,
        },
        websocketWatchdog: getWatchdogState("pong", lastPongAt),
      });
      clearTimeout(postTimeout);
      return;
    }

    let data: any;
    try {
      data = JSON.parse(e.data);
    } catch (e) {
      console.log("JSON parse error", e);
      wsDebug("websocket JSON parse error", {
        raw: typeof e?.toString === "function" ? e.toString() : "unknown",
      });
      return;
    }
    if (data?.cmd === "COMP" || data?.cmd === "ONLI" || data?.cmd === "WS") {
      wsDebug("websocket message", data);
    }
    routeIncommingMessage(data);
  };

  websocket.sendPing = () => {
    try {
      lastPingAt = Date.now();
      websocket.send("ping");
      setPingErrorData({
        eventSequence: {
          lastPingSent: lastPingAt,
        },
        websocketWatchdog: getWatchdogState("ping", lastPingAt),
      });
    } catch (e) {
      setTimeout(() => {
        websocket.sendPing();
      }, 1000);
    }
  };
}

function startPing() {
  clearInterval(pingInterval);

  pingInterval = setInterval(() => {
    try {
      if (websocket && websocket.readyState == WebSocket.OPEN) {
        if (isPongStale()) {
          reconnectFromWatchdog("ping-interval:stale-pong");
          return;
        }
        websocket.sendPing();
      }
    } catch (e) {
      console.log(e);
    }
  }, PING_INTERVAL_MS);
}

// async function longPollingConnect() {
//   console.log("switchToLongPolling");
//   disconnectWebSocket();

//   useLongPolling = true;
//   const username = userStore.getState().username;
//   const sessionId = sessionStorage.getItem("sessionId");

//   const { gamePlayStore } = await import("../redux/gamePlay");
//   const { gameId } = gamePlayStore.getState();

//   await longPollingGetRequest(username, sessionId, gameId);
//   setWsConnected(true);
// }
